import { useRouter } from "next/router";

import { BRAND_PATHS, ORDER_STATUS_IDS, ORDER_TYPE_IDS } from "@chef/constants";
import {
  formatDate,
  formatTimeInterval,
  getNextCutoff,
  orderHasBeenDelivered,
  shouldShowDelivery,
} from "@chef/helpers";
import { useOrdersQuery } from "@chef/state-management";
import { useCalendarData } from "@chef/state-management/hooks";
import { clamp } from "@chef/utils/clamp";
import { getBasketForCalendarWeek } from "@chef/state-management/helpers";
import { isEmptyArray } from "@chef/utils/array";

import { intl } from "./WeeksNavigation.Intl";

type CalendarWeek = {
  type: "my-menu";
  order: number;
  week: number;
  year: number;
  href: string;
  deliveryDate: string;
  cutoffDateTime: string;
  message: string;
  status: string;
};

type OrderWeek = {
  type: "my-delivery";
  order: number;
  week: number;
  year: number;
  href: string;
  deliveryDate: string;
  cutoffDateTime?: never;
  message: string;
  status: string;
};

type Skeleton = {
  type: "skeleton";
  order: number;
};

type Separator = {
  type: "separator";
  order: number;
};

type None = {
  type: "none";
};

export type WeekBlock = CalendarWeek | OrderWeek | None | Separator | Skeleton;

class ReferencableWeekBlocks {
  private currentWeek: number;
  private currentYear: number;
  private range: number;

  private currentIdx: number;

  private nextIdx: number;
  private prevIdx: number;

  private weeks: (CalendarWeek | OrderWeek)[];
  private separatorIndices: number[];
  private skeletonIndices: number[];

  constructor(week: number, year: number, range: number) {
    this.weeks = [];
    this.separatorIndices = [];
    this.skeletonIndices = [];
    this.range = range;

    this.currentWeek = week;
    this.currentYear = year;

    this.currentIdx = -1;
    this.nextIdx = -1;
    this.prevIdx = -1;
  }

  get isReady() {
    return this.currentIdx !== -1;
  }

  add(week: CalendarWeek | OrderWeek) {
    this.weeks.push(week);

    this.weeks = [...this.weeks].sort((a, b) => {
      const aDate = new Date(a.deliveryDate);
      const bDate = new Date(b.deliveryDate);
      return aDate.getTime() - bDate.getTime();
    });

    const idx = this.weeks.findIndex(
      (w) => w.week === this.currentWeek && w.year === this.currentYear,
    );

    if (idx !== -1) {
      this.currentIdx = idx;
      this.nextIdx = clamp(idx + 1, 0, this.weeks.length - 1);
      this.prevIdx = clamp(idx - 1, 0, this.weeks.length - 1);
    }
  }

  separator(idx: number) {
    this.separatorIndices.push(idx);
  }

  skeleton(idx: number) {
    this.skeletonIndices.push(idx);
  }

  render() {
    const result: WeekBlock[] = [];

    for (let i = 0; i < this.range; i++) {
      if (this.skeletonIndices.includes(i)) {
        result.push({ type: "skeleton", order: i });
      }

      if (this.separatorIndices.includes(i)) {
        result.push({ type: "separator", order: 1 });
      }

      const _week = this.weeks.find((w) => w.order === i);

      if (_week) {
        result.push(_week);
      }
    }

    return result;
  }

  get current(): CalendarWeek | OrderWeek {
    return this.weeks[this.currentIdx];
  }

  get next(): CalendarWeek | OrderWeek | None {
    if (this.nextIdx === this.currentIdx) {
      return { type: "none" };
    }

    return this.weeks[this.nextIdx];
  }

  get prev(): CalendarWeek | OrderWeek | None {
    if (this.prevIdx === this.currentIdx) {
      return { type: "none" };
    }

    return this.weeks[this.prevIdx];
  }
}

export const useWeeks = () => {
  const router = useRouter();
  const nextCutoff = getNextCutoff();
  const CALENDAR_RANGE = 4;
  const ORDERS_RANGE = 2;

  const { calendar, isLoading: isCalendarLoading } = useCalendarData({
    week: nextCutoff.week,
    year: nextCutoff.year,
    range: CALENDAR_RANGE,
  });

  const week = +(router.query["week"] ?? calendar?.[0]?.week);
  const year = +(router.query["year"] ?? nextCutoff.year);

  const { data: ordersQuery, isLoading: isOrdersLoading } = useOrdersQuery({
    count: ORDERS_RANGE,
    orderTypes: [ORDER_TYPE_IDS.RECURRING],
    orderStatuses: [ORDER_STATUS_IDS.PROCESSING, ORDER_STATUS_IDS.FINISHED],
  });

  const orders = ordersQuery?.orders ?? [];

  const blocks = new ReferencableWeekBlocks(
    week,
    year,
    CALENDAR_RANGE + ORDERS_RANGE,
  );

  if (isOrdersLoading) {
    for (let i = 0; i < ORDERS_RANGE; i++) {
      blocks.skeleton(i);
    }
  }

  let numShownOrders = 0;
  let ordersSkipped = 0;

  for (let i = 0; i < orders.length; i++) {
    const order = orders[orders.length - 1 - i];

    if (!shouldShowDelivery(order.deliveryDate)) {
      ordersSkipped += 1;
      continue;
    }

    numShownOrders += 1;

    const data = {
      type: "my-delivery",
      order: i - ordersSkipped,
      week: order.week,
      year: order.year,
      href: BRAND_PATHS.MY_DELIVERY({
        week: order.week,
        year: order.year,
      }),
      message: `${intl.WEEK} ${order.week}`,
      deliveryDate: order.deliveryDate,
      status: orderHasBeenDelivered(order) ? intl.DELIVERED : intl.IN_TRANSIT,
    } as const;

    blocks.add(data);
  }

  const currentNumBlocks = isOrdersLoading ? ORDERS_RANGE : numShownOrders;

  if (currentNumBlocks) {
    blocks.separator(currentNumBlocks);
  }

  const numCalendarWeeksToShow = 3;
  const calendarWeeks =
    calendar?.filter(Boolean).slice(0, numCalendarWeeksToShow) || [];

  const calendarStartIndex = isOrdersLoading ? ORDERS_RANGE : numShownOrders;

  if (isEmptyArray(calendarWeeks)) {
    for (let i = 0; i < numCalendarWeeksToShow; i++) {
      blocks.skeleton(calendarStartIndex + i);
    }
  }

  for (let i = 0; i < calendarWeeks.length; i++) {
    const calendarWeek = calendarWeeks[i];
    const { mealboxBasket } = getBasketForCalendarWeek({ calendarWeek });

    if (!mealboxBasket) {
      continue;
    }

    const shippingDetails = mealboxBasket.shippingDetails;

    const deliveryInformation = shippingDetails.plannedDeliveryInformation;

    const isPaused = !shippingDetails.hasDelivery;

    const isUndeliverable = !deliveryInformation.isAvailableForDelivery;

    let deliveryMessage: string;

    if (isUndeliverable) {
      deliveryMessage = intl.CLOSED;
    } else if (isPaused) {
      deliveryMessage = intl.CANCELLED;
    } else {
      const deliveryDate = new Date(deliveryInformation.deliveryDate);

      const formattedDeliveryWindow = formatTimeInterval(
        deliveryInformation.from,
        deliveryInformation.to,
        "m",
      );

      const deliveryDateMessage = formatDate(
        deliveryDate,
        intl.SHORT_DATE_FORMAT,
      );

      deliveryMessage = `${deliveryDateMessage} ${formattedDeliveryWindow}`;
    }

    const data = {
      type: "my-menu",
      order: calendarStartIndex + i,
      week: calendarWeek.week,
      year: calendarWeek.year,
      deliveryDate: deliveryInformation.deliveryDate,
      cutoffDateTime: deliveryInformation.cutOffDateTime,
      href: BRAND_PATHS.MY_MENU_WEEK_HREF({
        week: calendarWeek.week,
        year: calendarWeek.year,
      }),
      message: `${intl.WEEK} ${calendarWeek.week}`,
      status: deliveryMessage,
    } as const;

    blocks.add(data);
  }

  return {
    weeks: blocks,
    isCalendarLoading,
    isOrdersLoading,
  };
};
